// Shared label module — single source of truth for any human-facing
// label/text that has historically lived in two places (the React app under
// src/ and the Deno-rendered email templates under
// supabase/functions/_shared/transactional-email-templates/).
//
// Rationale (Refactor A + B + D, 2026-06-17):
// The free-scan flow has bitten us multiple times because the same fact
// (status keywords, scan-failure labels, the canonical failure-reason union)
// was duplicated across files with nothing forcing them to agree:
//
//   - PR #336 updated the web statusLabelFr() but not the email's, so
//     "partial" / "good" English keywords shipped to customer inboxes.
//   - PR #354 had to retroactively add a "policy_flagged" entry to the
//     email-side SCAN_FAILURE_LABELS that the web had carried since
//     Phase A.1 — PostFinance customers got the generic "Scan incomplet"
//     copy until we noticed.
//   - The web SCAN_FAILURE_LABELS itself is still missing fetch_blocked,
//     waf_blocked, unsupported_language and rate_limited (handled
//     elsewhere via BUCKET_B_REASON_TEXT, but the drift was invisible).
//
// Everything in this file is plain TypeScript with no Deno- or
// browser-specific imports, so both runtimes can read it without
// adapter layers. The parity test at labels.test.ts asserts that
// SCAN_FAILURE_REASONS ⊆ keys(SCAN_FAILURE_LABELS), so a future PR that
// adds a new reason but forgets the label fails at npm test instead of
// at customer.

// ─────────────────────────────────────────────────────────────────────────
// Canonical ScanFailureReason union
//
// This is the *single source of truth* for what may appear on the wire
// in report.scanFailureReason. The runtime validator in
// src/lib/audit-response.ts imports the list from here so a new value
// added at the backend can never be silently dropped by the frontend
// reconciler (the PR #112 / fetch_blocked-was-invisible class of bug).
// ─────────────────────────────────────────────────────────────────────────

export const SCAN_FAILURE_REASONS = [
  "cookie_wall",
  "ssl_failure",
  "timeout",
  "http_404",
  "anti_bot",
  "client_side_render",
  "budget_exceeded",
  "policy_not_found",
  // PR #112 — backend-emitted granular network-failure reasons.
  "fetch_blocked",
  "waf_blocked",
  // PR fix/route-non-french-to-bucket-b (2026-05-14).
  "unsupported_language",
  // Frontend-only synthetic — Index.tsx builds a local AuditReport with
  // this reason when the submit endpoint returns HTTP 429.
  "rate_limited",
  // Phase A.2 (Mistral migration, 2026-06) — one of the four policy
  // prechecks fired (POLICY_TOO_SHORT / POLICY_TOO_LONG / WRONG_LANGUAGE /
  // NOT_A_POLICY). Sub-reason lives on report.policyFlagReason and the
  // customer-facing French message on report.policyFlagMessage.
  "policy_flagged",
  // Phase A.2 — Mistral extractor itself failed (timeout / non-2xx / parse
  // fail / schema violation on both passes). Distinct from policy_flagged
  // because the policy was fine; only our call failed.
  "extractor_unavailable",
  "unknown",
] as const;

export type ScanFailureReason = (typeof SCAN_FAILURE_REASONS)[number];

export const isScanFailureReason = (
  s: string | null | undefined,
): s is ScanFailureReason =>
  !!s && (SCAN_FAILURE_REASONS as readonly string[]).includes(s);

// ─────────────────────────────────────────────────────────────────────────
// Section status label (Refactor A)
//
// The Mistral pipeline emits 'good' / 'partial' / 'poor' / 'n/a' on the
// wire. Render in French everywhere the customer sees it (report page,
// email body, admin drill-down).
// ─────────────────────────────────────────────────────────────────────────

export function statusLabelFr(status: string | null | undefined): string {
  switch (status) {
    case "good":
      return "Bon";
    case "partial":
      return "Partiel";
    case "poor":
      return "À améliorer";
    case "n/a":
      return "N/A";
    default:
      return status ?? "";
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Scan failure labels (Refactor B)
//
// Title + explanatory copy per failure reason. Honesty pass language is
// preserved — see audit-warnings.ts header comment for the framing
// (no "discovery", "crawler" or "policy URL" phrasing).
// ─────────────────────────────────────────────────────────────────────────

export interface ScanFailureLabel {
  title: string;
  explain: string;
}

export const SCAN_FAILURE_LABELS: Record<ScanFailureReason, ScanFailureLabel> = {
  cookie_wall: {
    title: "Cookie wall ou contenu non chargé",
    explain:
      "Notre scan technique n'a pas pu accéder à votre page d'accueil (bandeau de consentement obligatoire ou rendu côté client). Les éléments comportementaux (cookies, traceurs, CMP) peuvent donc être incomplets. L'analyse juridique de votre politique se fait à partir du texte que vous collez ci-dessous.",
  },
  ssl_failure: {
    title: "Certificat SSL inaccessible",
    explain:
      "Notre scan technique n'a pas pu établir une connexion sécurisée à votre page d'accueil (certificat invalide ou nom d'hôte non résolu).",
  },
  timeout: {
    title: "Site trop lent à répondre",
    explain: "Le site n'a pas répondu dans le délai imparti (30 secondes).",
  },
  http_404: {
    title: "URL introuvable (404)",
    explain:
      "L'URL que vous avez soumise renvoie une erreur 404. Vérifiez l'adresse et relancez l'analyse.",
  },
  anti_bot: {
    title: "Protection anti-bot active",
    explain:
      "Une protection (Cloudflare, captcha, reCAPTCHA) a bloqué notre scan technique avant qu'il puisse lire votre page d'accueil.",
  },
  client_side_render: {
    title: "Site rendu côté client (SPA)",
    explain:
      "Votre site charge la majorité de son contenu en JavaScript après l'arrivée du visiteur. Notre scan technique n'a vu qu'un squelette HTML, ce qui peut affecter la détection du bandeau cookies, des traceurs et des cookies. L'analyse juridique de votre politique se fait à partir du texte que vous collez ci-dessous.",
  },
  budget_exceeded: {
    title: "Analyse interrompue",
    explain:
      "Notre scan a dépassé son budget d'analyse pour ce site (politique volumineuse ou multiples nouvelles tentatives). Réessayez plus tard ou collez le texte de votre politique pour un scan ciblé.",
  },
  policy_not_found: {
    title: "Analyse juridique en attente",
    explain:
      "L'analyse juridique de votre politique de confidentialité se base uniquement sur le texte que vous nous fournissez. Pour obtenir l'évaluation, relancez l'analyse en collant le texte de votre politique ci-dessous.",
  },
  fetch_blocked: {
    title: "Accès à votre site bloqué",
    explain:
      "Notre scan technique a été refusé par votre serveur (protection IP, géolocalisation ou règle de pare-feu). L'analyse juridique de votre politique se fait à partir du texte que vous collez ci-dessous.",
  },
  waf_blocked: {
    title: "Pare-feu applicatif (WAF) actif",
    explain:
      "Un pare-feu applicatif (WAF) a bloqué notre scan technique avant qu'il puisse lire votre page d'accueil. L'analyse juridique de votre politique se fait à partir du texte que vous collez ci-dessous.",
  },
  unsupported_language: {
    title: "Site en langue non prise en charge",
    explain:
      "Le scan gratuit analyse uniquement les politiques en français. Collez la version française de votre politique de confidentialité pour relancer l'analyse, ou utilisez le Dossier Pro nLPD pour une analyse multilingue.",
  },
  rate_limited: {
    title: "Trop de scans récents depuis votre IP",
    explain:
      "Pour préserver la qualité du service, nous limitons le nombre de scans par adresse IP. Réessayez dans quelques minutes.",
  },
  // Phase A.2 — generic copy; the specific sub-reason (POLICY_TOO_SHORT /
  // POLICY_TOO_LONG / WRONG_LANGUAGE / NOT_A_POLICY) is on the wire as
  // report.policyFlagReason and the customer-facing French message is on
  // report.policyFlagMessage. UI surfaces should prefer those over this
  // fallback when available.
  policy_flagged: {
    title: "Texte non analysé",
    explain:
      "Le texte collé n'a pas pu être analysé (longueur, langue ou contenu non attendu). Collez la politique de confidentialité en français (entre 800 et 25 000 caractères) pour relancer l'analyse.",
  },
  extractor_unavailable: {
    title: "Analyse temporairement indisponible",
    explain:
      "Notre analyse automatique n'a pas pu traiter votre politique pour le moment. Réessayez dans quelques minutes ou commandez le Dossier Pro nLPD pour une revue complète.",
  },
  unknown: {
    title: "Scan incomplet",
    explain:
      "Notre scan technique n'a pas pu collecter assez de données comportementales sur votre site. L'analyse juridique se fait à partir du texte que vous collez ci-dessous.",
  },
};

/** Resolve label by reason, falling back to "unknown". */
export function getScanFailureMeta(
  reason: string | null | undefined,
): ScanFailureLabel {
  if (!reason) return SCAN_FAILURE_LABELS.unknown;
  if (isScanFailureReason(reason)) return SCAN_FAILURE_LABELS[reason];
  return SCAN_FAILURE_LABELS.unknown;
}
